// Player Spawning Logic
// Extracted from Game.jsx for better organization

import { Player } from '../Player';
import BattleStateManager from './BattleStateManager';
import { StatNormalizationService } from './StatNormalizationService';

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

// Container definitions (matching Player.js)
const CONSUMABLE_CONTAINERS = [
  {
    key: 'healing_container',
    matches: (itemDef) =>
      !!itemDef &&
      ((itemDef.effect && typeof itemDef.effect === 'object' && itemDef.effect.heal != null) ||
        itemDef.type === 'healing'),
  },
  {
    key: 'magic_container',
    matches: (itemDef) =>
      !!itemDef &&
      ((itemDef.effect && typeof itemDef.effect === 'object' && itemDef.effect.mana != null) ||
        itemDef.type === 'mana'),
  },
  {
    key: 'battle_container',
    matches: (itemDef) =>
      !!itemDef &&
      ((itemDef.effect &&
        typeof itemDef.effect === 'object' &&
        (itemDef.effect.attackBoost != null ||
          itemDef.effect.rage != null)) ||
        itemDef.type === 'strength' ||
        itemDef.type === 'battle'),
  },
  {
    key: 'arrow_container',
    matches: (itemDef) =>
      !!itemDef &&
      ((itemDef.effect &&
        typeof itemDef.effect === 'object' &&
        itemDef.effect.arrows != null) ||
        itemDef.type === 'ammo' ||
        itemDef.type === 'arrow'),
  },
  {
    key: 'poison_container',
    matches: (itemDef) =>
      !!itemDef &&
      ((itemDef.effect &&
        typeof itemDef.effect === 'object' &&
        (itemDef.effect.dot != null || itemDef.effect.poison != null)) ||
        itemDef.type === 'poison'),
  },
];

function determineConsumableContainer(itemDef) {
  if (!itemDef) return null;
  for (const container of CONSUMABLE_CONTAINERS) {
    if (container.matches(itemDef)) {
      return container.key;
    }
  }
  return null;
}

// Limit INCOMING inventory to max items per container per match
// This only limits what players bring INTO the match, not loot picked up during the match
export function limitInventoryPerContainer(inventory, itemsById, maxStack = 10) {
  if (!Array.isArray(inventory) || !itemsById) return inventory;
  
  const MAX_QUANTITY_PER_ITEM = 99;
  const MAX_INCOMING_ITEMS_PER_CONTAINER = Math.max(0, Math.min(99, Math.floor(maxStack))); // Limit for incoming items only (originalQty)
  const containerGroups = new Map(); // containerKey -> Map<itemId, aggregatedEntry>
  
  // Group items by container and aggregate by itemId
  inventory.forEach(entry => {
    const itemDef = itemsById.get(entry.itemId);
    const containerKey = determineConsumableContainer(itemDef) || entry.equipmentSlot || entry.equipment_slot || 'general';
    
    if (!containerGroups.has(containerKey)) {
      containerGroups.set(containerKey, new Map());
    }
    
    const containerItems = containerGroups.get(containerKey);
    const itemId = entry.itemId || entry.item_id;
    const currentQty = Math.max(0, toNumber(entry.qty ?? entry.quantity ?? 0, 0));
    
    if (!itemId || currentQty <= 0) return;
    
    // Track originalQty (incoming) separately from lootQty
    const incomingQty = Math.max(0, toNumber(entry.originalQty ?? currentQty, currentQty));
    const lootQty = Math.max(0, toNumber(entry.lootQty ?? 0, 0));
    
    if (containerItems.has(itemId)) {
      // Aggregate quantities for same itemId
      const existing = containerItems.get(itemId);
      const existingQty = Math.max(0, toNumber(existing.qty ?? existing.quantity ?? 0, 0));
      const existingIncomingQty = Math.max(0, toNumber(existing.originalQty ?? 0, 0));
      const existingLootQty = Math.max(0, toNumber(existing.lootQty ?? 0, 0));
      
      // Aggregate: incoming items are limited, loot items are not
      const newIncomingQty = existingIncomingQty + incomingQty;
      const newLootQty = existingLootQty + lootQty;
      const newTotalQty = Math.min(newIncomingQty + newLootQty, MAX_QUANTITY_PER_ITEM);
      
      existing.qty = newTotalQty;
      existing.quantity = newTotalQty;
      existing.originalQty = newIncomingQty; // Track incoming separately
      existing.lootQty = newLootQty; // Track loot separately
    } else {
      // First entry for this itemId in this container
      const aggregated = {
        ...entry,
        qty: Math.min(currentQty, MAX_QUANTITY_PER_ITEM),
        quantity: Math.min(currentQty, MAX_QUANTITY_PER_ITEM),
        originalQty: incomingQty, // Preserve incoming quantity
        lootQty: lootQty, // Preserve loot quantity
        equipmentSlot: containerKey !== 'general' ? containerKey : (entry.equipmentSlot ?? entry.equipment_slot ?? null),
        slotNumber: null, // No slot tracking for consumables
        slot_number: null
      };
      containerItems.set(itemId, aggregated);
    }
  });
  
  const limitedInventory = [];
  
  // Process each container group
  containerGroups.forEach((itemsMap, containerKey) => {
    if (containerKey === 'general' || !containerKey) {
      // Non-container items: keep all (but still aggregate by itemId)
      itemsMap.forEach(entry => limitedInventory.push(entry));
      return;
    }
    
    // Calculate total INCOMING quantity (originalQty) in this container
    let totalIncomingQty = 0;
    const entries = Array.from(itemsMap.values());
    entries.forEach(entry => {
      totalIncomingQty += Math.max(0, toNumber(entry.originalQty ?? 0, 0));
    });
    
    // Apply 10-item limit ONLY to incoming items (originalQty), not loot
    if (totalIncomingQty <= MAX_INCOMING_ITEMS_PER_CONTAINER) {
      // Under limit: keep all entries as-is
      entries.forEach(entry => {
        const qty = Math.max(0, toNumber(entry.qty ?? entry.quantity ?? 0, 0));
        if (qty > 0) {
          limitedInventory.push(entry);
        }
      });
    } else {
      // Over limit: reduce incoming quantities (originalQty) to max 10, but preserve loot
      let remainingIncoming = MAX_INCOMING_ITEMS_PER_CONTAINER;
      for (const entry of entries) {
        const currentIncomingQty = Math.max(0, toNumber(entry.originalQty ?? 0, 0));
        const currentLootQty = Math.max(0, toNumber(entry.lootQty ?? 0, 0));
        
        if (remainingIncoming <= 0 && currentIncomingQty > 0) {
          // No more incoming items allowed, but keep loot
          if (currentLootQty > 0) {
            const lootOnlyEntry = {
              ...entry,
              qty: currentLootQty,
              quantity: currentLootQty,
              originalQty: 0, // No incoming items
              lootQty: currentLootQty,
            };
            limitedInventory.push(lootOnlyEntry);
          }
          continue;
        }
        
        const allowedIncomingQty = Math.min(currentIncomingQty, remainingIncoming);
        const totalQty = allowedIncomingQty + currentLootQty;
        
        if (totalQty > 0) {
          const limitedEntry = {
            ...entry,
            qty: totalQty,
            quantity: totalQty,
            originalQty: allowedIncomingQty, // Limited incoming
            lootQty: currentLootQty, // Preserve all loot
          };
          limitedInventory.push(limitedEntry);
          remainingIncoming -= allowedIncomingQty;
        }
      }
    }
  });
  
  return limitedInventory;
}

const normalizeInventoryEntry = (entry) => {
  if (!entry) return { itemId: null, qty: 0, quantity: 0 };
  const clone = { ...entry };
  clone.itemId = clone.itemId ?? clone.item_id ?? null;
  clone.item_id = clone.itemId;
  clone.itemType = clone.itemType ?? clone.item_type ?? null;
  clone.item_type = clone.itemType;
  clone.equipmentSlot = clone.equipmentSlot ?? clone.equipment_slot ?? null;
  clone.equipment_slot = clone.equipmentSlot;
  clone.slotNumber = clone.slotNumber ?? clone.slot_number ?? null;
  clone.slot_number = clone.slotNumber;
  clone.iconKey = clone.iconKey ?? clone.icon_key ?? null;
  clone.icon_key = clone.iconKey;

  const quantity = Math.max(
    0,
    toNumber(clone.quantity ?? clone.qty ?? clone.amount, 0)
  );
  clone.qty = quantity;
  clone.quantity = quantity;

  clone.initialQty = Math.max(0, toNumber(clone.initialQty, quantity));
  clone.originalQty = Math.max(0, toNumber(clone.originalQty, clone.initialQty));
  clone.lootQty = Math.max(0, toNumber(clone.lootQty, 0));
  clone.consumedOriginal = Math.max(0, toNumber(clone.consumedOriginal, 0));
  clone.consumedLoot = Math.max(0, toNumber(clone.consumedLoot, 0));

  return clone;
};

export class PlayerSpawner {
  constructor(scene) {
    this.scene = scene;
    this.players = [];
    this.activeHeroes = new Map(); // wallet -> hero data
    this.heroes = []; // Track all heroes for average level calculation
  }

  // Fetch active Hero for a wallet address
  async fetchActiveHero(walletAddress) {
    try {
      // First get user by wallet
      const userResponse = await fetch(`/api/user-lookup/by-wallet/solana/${encodeURIComponent(walletAddress)}`);
      const userData = await userResponse.json();
      
      if (!userData.success || !userData.user) {
        return null;
      }

      // Get user's active Hero
      const warriorsResponse = await fetch(`/api/heroes/user/${userData.user.id}`);
      const warriorsData = await warriorsResponse.json();
      
      if (!warriorsData.success || !warriorsData.data) {
        return null;
      }

      // Find active hero
      const activeWarrior = warriorsData.data.find(hero => hero.is_active);
      return activeWarrior || null;
    } catch (error) {
      console.error('Error fetching Hero:', error);
      return null;
    }
  }

  // Spawn a player with Hero data (accepts pre-fetched hero data to avoid duplicate API calls)
  async spawnPlayer(x, y, textureKey, name, walletAddress = null, heroData = null, teamId = null) {
    const player = new Player(this.scene, x, y, textureKey, name, walletAddress);
    player.userId = heroData?.userId || null;

    //console.log('[PlayerSpawner] Spawning player:', x, y, textureKey, name, walletAddress, heroData ? 'with hero data' : 'no hero data');
    //console.log('[PlayerSpawner] Spawning player. heroData:', heroData);

    // Apply hero data if provided, otherwise fetch it
    let hero = heroData;
    /*if (!hero && walletAddress) {
      hero = await this.fetchActiveHero(walletAddress);
    }*/

    //activate the player's equipped items
    if (hero && Array.isArray(hero.inventory) && hero.inventory.length > 0) {
      hero.inventory.forEach(item => {
        const key = item.item_id || item.itemId;
        if (key && this.scene.itemsById && this.scene.itemsById.get) {
          // const def = this.scene.itemsById.get(key);
          //console.log('[PlayerSpawner] Activating item:', key);
        }
      });
    }



    if (hero) {
      //console.log('[PlayerSpawner] spanwPlayer Applying hero stats to player:', hero);
      this.applyHeroStats(player, hero);
      //this.activeHeroes.set(walletAddress, hero);
      //console.log('[PlayerSpawner] Hero stats applied to player:', player);

      // Join battle once for real users (skip pure AI wallets)
      if (this.scene && this.scene.gameManager && this.scene.gameManager.currentBattleId) {
        //console.log('[PlayerSpawner] Joining battle for hero:', hero.id);
        const isAIWallet = typeof walletAddress === 'string' && walletAddress.startsWith('ai_wallet_');
        //console.log('[PlayerSpawner] Is AI wallet:', isAIWallet);
        if (!isAIWallet) {
          try {
            //console.log('[PlayerSpawner] Joining battle for hero:', hero.id);
            //console.log(`🔗 Joining battle for hero ${hero.id} (${player.name})`);
            //Make the API call to join the battle
            //await this.scene.gameManager.ensureHeroJoined(hero.id, teamId || player.teamId || null);
            //console.log('[PlayerSpawner] Battle joined for hero:', hero.id);
          } catch (e) {
            //console.warn('Failed to join battle for hero', hero.id, e);
          }
        }
      }
    }



    //console.log('[PlayerSpawner] Player spawned:', player);

    this.players.push(player);

    // Per-player item checks now handled in Player.update via elapsed time

    return player;
  }

  // Apply Hero attributes to player stats
  applyHeroStats(player, hero) {

    //console.log('[PlayerSpawner] Applying hero stats to player. player:', player);
    //console.log('[PlayerSpawner] Applying hero stats to player. hero:', hero);
    // Store Hero ID for battle recording
    player.heroId = hero.id;
    // Attach minimal hero info for UI panels
    player.hero = {
      id: hero.id,
      isJoined: hero.isJoined,
      isAI: hero.isAI,
      name: hero.name,
      image_url: hero.image_url || null,
      level: hero.level || 1
    };
    
    // Store hero for normalization context
    if (!this.heroes.find(h => h.id === hero.id)) {
      this.heroes.push(hero);
    }
    
    const normalizedInventory = Array.isArray(hero.inventory)
      ? hero.inventory.map((entry) => normalizeInventoryEntry(entry))
      : [];
    
    // Limit to maxBattleStack items per container per match
    // Use nullish coalescing to allow 0 as a valid value
    const maxStack = BattleStateManager?.maxBattleStack ?? 10;
    const limitedInventory = limitInventoryPerContainer(
      normalizedInventory, 
      this.scene?.itemsById,
      maxStack
    );
    
    player.hero.inventory = limitedInventory;
    player.inventory = player.hero.inventory;
    if (typeof player.initializeInventory === 'function') {
      player.initializeInventory(player.inventory);
    }
    
    // Get battle config for normalization settings
    const battleConfig = this.scene?.battleConfig || 
                        BattleStateManager?.battleConfig || 
                        this.scene?.gameManager?.battleConfig ||
                        {};
    
    const normalizationConfig = battleConfig.statNormalization || null;

    // Normalize stats if enabled
    let normalizedStats = {
      strength: hero.strength || 10,
      endurance: hero.endurance || 10,
      coordination: hero.coordination || 10,
      quickness: hero.quickness || 10,
      focus: hero.focus || 10,
      self: hero.self || 10,
    };

    if (normalizationConfig && normalizationConfig.enabled) {
      // Calculate average level for level-based normalization
      const averageLevel = StatNormalizationService.calculateAverageLevel(this.heroes);
      
      normalizedStats = StatNormalizationService.normalizeHeroStats(
        hero,
        normalizationConfig,
        { averageLevel, allHeroes: this.heroes }
      );

      // Mark player as having normalized stats (for ultimate damage scaling)
      player.statsNormalized = true;

      // Log normalization for debugging
      if (normalizationConfig.debug) {
        console.log(`[StatNormalization] ${hero.name} (L${hero.level}):`, {
          original: {
            strength: hero.strength,
            endurance: hero.endurance,
            coordination: hero.coordination,
            quickness: hero.quickness,
          },
          normalized: normalizedStats,
          averageLevel,
        });
      }
    } else {
      // No normalization
      player.statsNormalized = false;
    }
    
    // Store primary attributes (now normalized)
    player.strength = normalizedStats.strength;
    player.endurance = normalizedStats.endurance;
    player.coordination = normalizedStats.coordination;
    player.quickness = normalizedStats.quickness;
    player.focus = normalizedStats.focus;
    player.self = normalizedStats.self;
    
    // Apply attribute-based stat modifications
    // Recalculate baseHealth using normalized stats for consistency with normalization
    // Formula matches HeroStatsService: 250 + (endurance / 2) + (strength / 2)
    const baseHealth = 250 + Math.floor((normalizedStats.endurance || 10) / 2) + Math.floor((normalizedStats.strength || 10) / 2);
    const baseSpeed = 20;
    
    // Health = base + (Endurance * 2) + (Strength * 1) + bonus for joined players
    // Use normalized stats for health calculation
    const enduranceBonus = (normalizedStats.endurance || 10) * 2;
    const strengthBonus = (normalizedStats.strength || 10) * 1;
    // Joined player bonus: 50% of base health (calculated from normalized stats)
    const joinedPlayerBonus = hero.isJoined ? Math.floor(baseHealth * 0.5) : 0;
    //console.log('[PlayerSpawner] joinedPlayerBonus:', joinedPlayerBonus);

    player.maxHealth = baseHealth + enduranceBonus + strengthBonus + joinedPlayerBonus;
    player.health = player.maxHealth;

    //console.log('[PlayerSpawner] Player incoming health:', player.health, 'baseHealth:', baseHealth, 'player.maxHealth:', player.maxHealth);
    
    // Move speed = base + speed bonus * multiplier
    // Original code used hero.speed directly (which is often Math.floor(quickness))
    // To maintain backwards compatibility while respecting Casual Mode normalization,
    // use normalized quickness directly (matching original behavior where hero.speed = quickness)
    // Joined players get a higher multiplier for speed
    const speedMultiplier = hero.isJoined ? 1.25 : 0.75; // Joined players: 1.25, AI: 0.75
    
    // Use normalized quickness directly (original code used hero.speed which was often raw quickness)
    // This ensures Casual Mode stat caps are respected while maintaining original speed values
    const speedValue = normalizedStats.quickness;
    const speedBonus = speedValue * speedMultiplier;
    
    player.moveSpeed = baseSpeed + speedBonus;
    player.baseMoveSpeed = player.moveSpeed; // Update baseMoveSpeed for speed modifiers
    
    // Apply strategy preferences to AI behavior
    player.strategyAggression = hero.strategy_aggression;
    player.strategyLooting = hero.strategy_looting;
    player.strategyPositioning = hero.strategy_positioning;
    // Map item usage strategy if available (0..100)
    if (typeof hero.strategy_item_use === 'number') {
      player.strategyItemUse = hero.strategy_item_use;
    }
    player.strategies = hero.strategies;
    player.stats = hero.stats;
    const heroStats = hero.stats || {};
    const derivedStamina = toNumber(heroStats.stamina ?? hero.stamina, 100);
    const derivedMana = toNumber(heroStats.mana ?? hero.mana, 100);
    if (Number.isFinite(derivedStamina)) {
      player.maxStamina = Math.max(0, derivedStamina);
      player.stamina = player.maxStamina;
    }
    if (Number.isFinite(derivedMana)) {
      player.maxMana = Math.max(0, derivedMana);
      player.mana = player.maxMana;
    }
    
    // Update player name to use Hero name if available
    if (hero.name) {
      player.name = hero.name;
      if (player.nameText) {
        player.nameText.setText(hero.name);
      }
    }

    if (typeof player.initializeUltimate === 'function') {
      player.initializeUltimate();
    }
    
    //console.log(`THIS IS PROBABLY WRONG Applied Hero stats to ${player.name}:`, player);
  }

  // Get all players
  getAllPlayers() {
    return this.players;
  }

  // Get alive players
  getAlivePlayers() {
    return this.players.filter(p => p.isAlive);
  }

  // Get player by wallet address
  getPlayerByWallet(walletAddress) {
    return this.players.find(p => p.walletAddress === walletAddress);
  }

  // Get Hero data for a wallet
  getHeroForWallet(walletAddress) {
    return this.activeHeroes.get(walletAddress);
  }

  // Clear all players
  clearPlayers() {
    this.players.forEach(player => {
      if (player.sprite) {
        player.sprite.destroy();
      }
    });
    this.players = [];
    this.activeHeroes.clear();
    this.heroes = []; // Clear heroes list for next battle
  }

  // Get battle results for Heroes
  getHeroBattleResults(battleDurationSeconds = null) {
    const results = [];

    const playersWithHero = this.players.filter(player => player.heroId && player.walletAddress);
    if (playersWithHero.length === 0) {
      return results;
    }

    const matchStartMs = (this.scene?.matchStartTime && this.scene.matchStartTime > 0) ? this.scene.matchStartTime : null;
    const nowMs = Date.now();

    const sortedPlayers = playersWithHero.slice().sort((a, b) => {
      if (a.isAlive && !b.isAlive) return -1;
      if (!a.isAlive && b.isAlive) return 1;

      const aOrder = typeof a.deathOrder === 'number' ? a.deathOrder : -Infinity;
      const bOrder = typeof b.deathOrder === 'number' ? b.deathOrder : -Infinity;
      if (aOrder !== bOrder) {
        return bOrder - aOrder;
      }

      const aTime = typeof a.timeAliveSeconds === 'number' ? a.timeAliveSeconds : (a.isAlive ? Number.POSITIVE_INFINITY : 0);
      const bTime = typeof b.timeAliveSeconds === 'number' ? b.timeAliveSeconds : (b.isAlive ? Number.POSITIVE_INFINITY : 0);
      if (aTime !== bTime) {
        return bTime - aTime;
      }

      const aElims = a.eliminations || 0;
      const bElims = b.eliminations || 0;
      if (aElims !== bElims) {
        return bElims - aElims;
      }

      return 0;
    });

    sortedPlayers.forEach((player, index) => {
      let timeAliveSeconds = (typeof player.timeAliveSeconds === 'number' && !Number.isNaN(player.timeAliveSeconds))
        ? player.timeAliveSeconds
        : null;

      if (player.isAlive) {
        if (typeof battleDurationSeconds === 'number') {
          timeAliveSeconds = battleDurationSeconds;
        } else if (matchStartMs) {
          timeAliveSeconds = (nowMs - matchStartMs) / 1000;
        }
      } else if (timeAliveSeconds == null && matchStartMs && typeof player.deathTimestamp === 'number') {
        timeAliveSeconds = (player.deathTimestamp - matchStartMs) / 1000;
      }

      if (typeof timeAliveSeconds === 'number' && !Number.isNaN(timeAliveSeconds)) {
        timeAliveSeconds = Math.round(Math.max(0, timeAliveSeconds) * 100) / 100;
      } else {
        timeAliveSeconds = null;
      }

      results.push({
        hero_id: player.heroId,
        wallet_address: player.walletAddress,
        final_position: index + 1,
        eliminations_count: player.eliminations || 0,
        damage_dealt: player.damageDealt || 0,
        damage_taken: player.damageTaken || 0,
        death_order: (typeof player.deathOrder === 'number' && !Number.isNaN(player.deathOrder)) ? player.deathOrder : null,
        time_alive: timeAliveSeconds,
        is_token_holder: false // TODO: Check token holder status
      });
    });

    return results;
  }
}
